import { Reveal } from "../lib/reveal";
import { Wind, Timer, FlaskConical, Sprout, ShieldCheck, TrendingDown } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";

const functional = [
  { icon: Wind, t: "Immediate odour reduction", th: "तत्काल गंध में कमी", d: "Measurable relief at the source within days of application.", dh: "आवेदन के कुछ ही दिनों में स्रोत पर मापने योग्य राहत।" },
  { icon: Timer, t: "Faster decomposition", th: "तेज़ अपघटन", d: "Accelerated breakdown of legacy and fresh organic waste.", dh: "पुराने व ताज़े जैविक अपशिष्ट का त्वरित विघटन।" },
  { icon: FlaskConical, t: "Lower methane & leachate", th: "कम मीथेन व लीचेट", d: "Reduced toxic gas generation and contamination risk.", dh: "विषैली गैस उत्पादन व संदूषण जोखिम में कमी।" },
  { icon: Sprout, t: "Restored biology", th: "पुनर्स्थापित जीव विज्ञान", d: "A living microbial ecosystem re-established on site.", dh: "साइट पर पुनः स्थापित जीवित सूक्ष्मजीवी परितंत्र।" },
];

const value = [
  { icon: TrendingDown, k: "Economic", kh: "आर्थिक", items: [["Lower landfill management cost", "लैंडफिल प्रबंधन लागत में कमी"], ["Faster recovery timelines", "तेज़ रिकवरी समयसीमा"], ["Reduced operational disruption", "कम परिचालन बाधा"]] },
  { icon: ShieldCheck, k: "Strategic", kh: "रणनीतिक", items: [["Swachh Bharat & ESG alignment", "स्वच्छ भारत व ESG अनुरूपता"], ["Better project compliance", "बेहतर परियोजना अनुपालन"], ["Improved public perception", "बेहतर सार्वजनिक धारणा"]] },
];

export const Solution = () => {
  const { t } = useLang();
  return (
    <section id="solution" data-testid="solution-section" className="py-24 md:py-32 bg-sand">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <div className="max-w-3xl">
          <Reveal className="flex items-center gap-3 mb-6">
            <span className="text-xs tracking-[0.22em] uppercase font-semibold text-moss">{t("The Solution · OED", "समाधान · OED")}</span>
          </Reveal>
          <Reveal>
            <h2 className="font-serif text-4xl md:text-5xl lg:text-6xl tracking-tight leading-[1.02] text-ink">
              {t("One microbial platform. A cascade of outcomes.", "एक सूक्ष्मजीवी मंच। परिणामों की एक श्रृंखला।")}
            </h2>
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-6 text-base md:text-lg leading-relaxed text-slate">
              {t(
                "Transform waste sites from odour-emitting liabilities into biologically active recovery ecosystems — functional, economic and strategic value in one solution.",
                "अपशिष्ट स्थलों को गंध फैलाने वाली देनदारियों से जैविक रूप से सक्रिय रिकवरी परितंत्रों में बदलें — एक ही समाधान में कार्यात्मक, आर्थिक व रणनीतिक मूल्य।"
              )}
            </p>
          </Reveal>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5">
          {functional.map((f, i) => (
            <Reveal key={f.t} delay={i * 0.07}>
              <div className="group h-full bg-bone rounded-2xl border border-stone/20 p-8 hover:border-moss transition-colors duration-400">
                <f.icon size={26} strokeWidth={1.4} className="text-moss group-hover:text-terracotta transition-colors duration-400" />
                <h3 className="font-serif text-2xl mt-6 mb-2 text-ink">{t(f.t, f.th)}</h3>
                <p className="text-sm leading-relaxed text-slate">{t(f.d, f.dh)}</p>
              </div>
            </Reveal>
          ))}
        </div>

        <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-5">
          {value.map((v, i) => (
            <Reveal key={v.k} delay={i * 0.1}>
              <div className="h-full bg-forest text-bone rounded-2xl p-8 md:p-10">
                <div className="flex items-center gap-3 mb-6">
                  <v.icon size={22} strokeWidth={1.4} className="text-terracotta" />
                  <span className="text-xs tracking-[0.18em] uppercase text-bone/60">{t(v.k, v.kh)} {t("Value", "मूल्य")}</span>
                </div>
                <ul className="space-y-4">
                  {v.items.map((it) => (
                    <li key={it[0]} className="flex items-baseline gap-3 font-serif text-2xl md:text-[1.7rem] leading-tight">
                      <span className="text-terracotta text-base">—</span>
                      {t(it[0], it[1])}
                    </li>
                  ))}
                </ul>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  );
};
